<template>
  <div class="space-y-6">
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 animate-fade-in-down">
      <div>
        <h1 class="text-3xl font-black tracking-tight text-slate-900">Demandes de regroupement</h1>
        <p class="mt-2 text-slate-600 font-medium">
          Commandes de plus de 3 colis : le regroupement doit être validé par un administrateur.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <button
          @click="fetchRequests"
          class="h-11 px-5 rounded-xl border border-primary/20 text-primary font-bold hover:bg-primary/5 transition-colors flex items-center gap-2"
          :disabled="loading"
        >
          <span class="material-symbols-outlined text-xl" :class="{ 'animate-spin': loading }">refresh</span>
          Actualiser
        </button>
      </div>
    </div>

    <div v-if="loading" class="grid grid-cols-1 gap-6">
      <div v-for="n in 3" :key="n" class="rounded-2xl bg-white border border-primary/10 p-6 h-24 skeleton-shimmer animate-fade-in" :style="{ animationDelay: `${n * 80}ms` }"></div>
    </div>

    <div v-else-if="error" class="rounded-2xl bg-red-50 border border-red-100 p-6 text-red-600 font-bold animate-fade-in-down">
      {{ error }}
    </div>

    <div v-else-if="requests.length === 0" class="rounded-3xl bg-white border border-primary/10 p-16 text-center animate-fade-in">
      <div class="size-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
        <span class="material-symbols-outlined text-5xl">groups</span>
      </div>
      <h3 class="text-xl font-black text-slate-900">Aucune demande en attente</h3>
      <p class="mt-2 text-slate-500 font-medium max-w-sm mx-auto">
        Les demandes de regroupement pour les commandes de plus de 3 colis apparaîtront ici.
      </p>
    </div>

    <div v-else class="grid grid-cols-1 gap-6">
      <div
        v-for="(req, idx) in requests"
        :key="req.id"
        class="rounded-2xl bg-white border border-primary/10 p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-0.5 transition-all duration-300 animate-fade-in-up"
        :style="{ animationDelay: `${Math.min(idx, 8) * 60}ms` }"
      >
        <div class="flex-1 space-y-1">
          <div class="text-xs font-black tracking-widest uppercase text-slate-400">
            Commande #{{ req.external_order_id || req.order_id || req.commande?.id }}
          </div>
          <div class="text-lg font-black text-slate-900">
            {{ req.commande?.num_facture || req.commande?.name || '—' }}
          </div>
          <div class="text-sm font-bold text-slate-500">
            {{ req.user?.name || req.client_name || 'Client' }} — {{ req.colis_count || 0 }} colis
          </div>
          <div class="text-xs font-bold text-slate-400">
            Demande envoyée le {{ formatDate(req.created_at) }}
          </div>
        </div>

        <div class="flex items-center gap-3">
          <span
            class="px-3 h-8 flex items-center rounded-lg text-xs font-black uppercase tracking-wider"
            :class="statusBadgeClass(req.status)"
          >
            {{ statusLabel(req.status) }}
          </span>
          <RouterLink
            :to="processLink(req)"
            class="h-10 px-4 rounded-xl bg-primary text-white font-black flex items-center gap-2 shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all"
          >
            <span class="material-symbols-outlined text-lg">open_in_new</span>
            Traiter
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'AdminGroupingRequestsPage',
  data() {
    return {
      loading: false,
      error: null,
      requests: []
    }
  },
  computed: {
    auth() {
      return useAuthStore()
    }
  },
  mounted() {
    this.fetchRequests()
  },
  methods: {
    async fetchRequests() {
      this.loading = true
      this.error = null
      try {
        const res = await axios.get('/ugo/grouping-requests', { params: { status: 'pending' } })
        const payload = res.data
        const raw = payload.data?.data || payload.data || []
        this.requests = Array.isArray(raw) ? raw : []
      } catch (e) {
        console.error('🚨 Erreur chargement des demandes de regroupement:', e)
        this.error = e.response?.data?.message || e.response?.data?.error || "Impossible de charger les demandes de regroupement."
      } finally {
        this.loading = false
      }
    },
    statusLabel(status) {
      const labels = { pending: 'En attente', approved: 'Approuvée', rejected: 'Rejetée', processed: 'Traitée' }
      return labels[status] || status || 'En attente'
    },
    statusBadgeClass(status) {
      if (status === 'approved' || status === 'processed') return 'bg-green-50 text-green-600'
      if (status === 'rejected') return 'bg-red-50 text-red-600'
      return 'bg-amber-50 text-amber-600'
    },
    processLink(req) {
      const id = req.external_order_id || req.order_id || req.commande?.id
      const userId = req.user?.id
      return userId ? { path: `/app/commandes/${id}`, query: { user_id: userId } } : `/app/commandes/${id}`
    },
    formatDate(value) {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleString('fr-FR')
      } catch (_) {
        return value
      }
    }
  }
}
</script>
